// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, ScrollView, StyleSheet, TextInput, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';

import { Text } from '@/components/Themed';
import GoffyButton from '@/components/GoffyButton';
import { useUser } from '@/app/user-context';
import { computeStrokesReceivedForHole, parseHandicapValue } from '@/features/hole/utils';
import type { LogRoundHole } from '@/features/log-round/types';
import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { getSharedBffWebSocket } from '@/services/bffWebSocket';
import { createRequestId } from '@/services/requestId';
import appColors from '@/theme/colors';

/** Hole-by-hole entry for a round played and finished before it was logged. */
export default function LogScorecardScreen() {
  const { roundId } = useLocalSearchParams<{ roundId?: string }>();
  const { user } = useUser();
  const { t } = useI18n();
  const router = useRouter();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);

  const [playerId, setPlayerId] = useState<string | null>(null);
  const [holes, setHoles] = useState<LogRoundHole[]>([]);
  const [playingHandicap, setPlayingHandicap] = useState<number | undefined>(undefined);
  const [scores, setScores] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.token || !user?.id || !roundId) return;
    const controller = new AbortController();
    const headers = { Authorization: `Bearer ${user.token}` };

    const load = async () => {
      try {
        const selfResponse = await fetch(`${apiBaseUrl}/api/player/by-user/${user.id}`, { headers, signal: controller.signal });
        const self = selfResponse.ok ? ((await selfResponse.json()) as { id?: string }) : null;
        const myPlayerId = self?.id ?? null;
        setPlayerId(myPlayerId);

        const roundResponse = await fetch(`${apiBaseUrl}/api/rounds/${roundId}`, { headers, signal: controller.signal });
        if (!roundResponse.ok) throw new Error('round');
        const round = (await roundResponse.json()) as { courseId?: string; CourseId?: string };
        const courseId = round.courseId ?? round.CourseId;
        if (!courseId) throw new Error('course');

        const courseResponse = await fetch(`${apiBaseUrl}/api/geodata/courses/${courseId}`, { headers, signal: controller.signal });
        if (!courseResponse.ok) throw new Error('holes');
        const course = (await courseResponse.json()) as {
          holes?: Array<{ number?: number; par?: number; strokeIndex?: number }>;
          Holes?: Array<{ Number?: number; Par?: number; StrokeIndex?: number }>;
        };
        const holeList = (course.holes ?? course.Holes ?? [])
          .map((hole) => ({
            number: Number((hole as { number?: number }).number ?? (hole as { Number?: number }).Number ?? 0),
            par: Number((hole as { par?: number }).par ?? (hole as { Par?: number }).Par ?? 0),
            strokeIndex: Number((hole as { strokeIndex?: number }).strokeIndex ?? (hole as { StrokeIndex?: number }).StrokeIndex ?? 0),
          }))
          .filter((hole) => hole.number > 0)
          .sort((a, b) => a.number - b.number);
        if (holeList.length === 0) throw new Error('noHoles');
        setHoles(holeList);

        if (myPlayerId) {
          const playersResponse = await fetch(`${apiBaseUrl}/api/rounds/${roundId}/players`, { headers, signal: controller.signal });
          if (playersResponse.ok) {
            type RoundPlayer = { id?: string; Id?: string; scp?: unknown; Scp?: unknown };
            // The BFF answers { players: [...] } (RoundEndpoints); tolerate a bare array too.
            const body = (await playersResponse.json()) as RoundPlayer[] | { players?: RoundPlayer[]; Players?: RoundPlayer[] };
            const players = Array.isArray(body) ? body : (body.players ?? body.Players ?? []);
            const me = players.find((player) => (player.id ?? player.Id) === myPlayerId);
            const parsed = parseHandicapValue(me?.scp ?? me?.Scp);
            setPlayingHandicap(parsed);
          }
        }
      } catch {
        if (!controller.signal.aborted) setLoadError(true);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    void load();
    return () => controller.abort();
  }, [apiBaseUrl, roundId, user?.id, user?.token]);

  const setHoleScore = (holeNumber: number, value: string) => {
    setScores((prev) => ({ ...prev, [holeNumber]: value }));
  };

  const sendScoreUpdate = (hole: LogRoundHole, grossStrokes: number) => {
    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setAuthToken(user?.token ?? null);
    socket.send({
      type: 'score.update',
      requestId: createRequestId(),
      roundId,
      playerId,
      hole: hole.number,
      par: hole.par,
      grossStrokes,
      pickup: false,
    });
  };

  const handleFinish = async () => {
    if (!roundId || !playerId) return;
    const entries = holes
      .map((hole) => ({ hole, value: Number.parseInt(scores[hole.number] ?? '', 10) }))
      .filter((entry) => Number.isFinite(entry.value) && entry.value > 0);
    if (entries.length === 0) {
      setError(t('logScorecard.enterAtLeastOne'));
      return;
    }

    setFinishing(true);
    setError(null);
    entries.forEach((entry) => sendScoreUpdate(entry.hole, entry.value));

    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setAuthToken(user?.token ?? null);
    // Score writes above are fire-and-forget (each confirmed independently by a hole.update
    // broadcast); this finish step only needs its own transition to land after them.
    setTimeout(() => {
      socket.send({ type: 'round.status.update', roundId, status: 'finished' });
      router.replace('/my-rounds');
    }, 600);
  };

  if (loading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="small" color={appColors.accent.primary} />
      </View>
    );
  }

  if (loadError || holes.length === 0) {
    return (
      <View style={styles.centered}>
        <Text style={styles.error}>{t('logScorecard.loadFailed')}</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.screen} contentContainerStyle={styles.content}>
      <View style={styles.headerRow}>
        <Text style={[styles.cell, styles.headerCell, styles.holeCell]}>{t('logScorecard.hole')}</Text>
        <Text style={[styles.cell, styles.headerCell, styles.parCell]}>{t('logScorecard.par')}</Text>
        <Text style={[styles.cell, styles.headerCell, styles.hcpCell]}>{t('logScorecard.hcpDot')}</Text>
        <Text style={[styles.cell, styles.headerCell, styles.scoreCell]}>{t('logScorecard.score')}</Text>
      </View>
      {holes.map((hole) => {
        const strokesReceived = computeStrokesReceivedForHole(playingHandicap ?? null, hole.strokeIndex);
        return (
          <View key={hole.number} style={styles.row} testID={`log-scorecard-hole-${hole.number}`}>
            <Text style={[styles.cell, styles.holeCell]}>{hole.number}</Text>
            <Text style={[styles.cell, styles.parCell]}>{hole.par}</Text>
            <Text style={[styles.cell, styles.hcpCell]}>{strokesReceived > 0 ? '•'.repeat(strokesReceived) : '—'}</Text>
            <TextInput
              style={[styles.cell, styles.scoreCell, styles.scoreInput]}
              value={scores[hole.number] ?? ''}
              onChangeText={(value) => setHoleScore(hole.number, value)}
              keyboardType="numeric"
              placeholder="-"
              placeholderTextColor={appColors.text.muted}
            />
          </View>
        );
      })}

      {error ? <Text style={styles.error}>{error}</Text> : null}

      <GoffyButton
        label={finishing ? t('logScorecard.finishing') : t('logScorecard.finishButton')}
        onPress={handleFinish}
        disabled={finishing}
        testID="log-scorecard-finish"
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: appColors.background.app,
  },
  content: {
    padding: 12,
    gap: 4,
    paddingBottom: 32,
  },
  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: appColors.background.app,
  },
  headerRow: {
    flexDirection: 'row',
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: appColors.border.strong,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
  },
  cell: {
    fontSize: 13,
    color: appColors.text.secondary,
  },
  headerCell: {
    fontWeight: '700',
    color: appColors.text.muted,
    fontSize: 11.5,
  },
  holeCell: {
    width: 40,
  },
  parCell: {
    width: 40,
  },
  hcpCell: {
    width: 50,
    color: appColors.accent.primary,
  },
  scoreCell: {
    flex: 1,
  },
  scoreInput: {
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panelAlt,
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    color: appColors.text.heading,
    fontWeight: '600',
  },
  error: {
    fontSize: 13,
    color: appColors.status.dangerText,
    marginVertical: 8,
  },
});
